import { Box, Button, makeStyles } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useIsInternalAdmin } from "Components/Members/hooks/useIsInternalAdmin";
import { useProjects } from "Components/Project/hooks/useProjects";
import { getCalendarEntryFromSchedulePhase } from "Components/SchedulePhase/SchedulePhaseHelper";
import { useSchedulePhaseModal } from "Components/SchedulePhase/hooks/useSchedulePhaseModal";
import { useSchedulePhases } from "Components/SchedulePhase/hooks/useSchedulePhases";
import { color, px } from "Configs/theme";
import {
	IsCalendarSubHeadOpen,
	SetIsCalendarSubHeadOpen,
} from "Helpers/LocalStorageHelper";
import { useTranslation } from "Providers/TranslationProvider";
import ErrorView from "Views/ErrorView";
import LoadingIndicator from "Views/LoadingIndicator";
import { Project, ProjectType, SchedulePhase } from "domain-entities";
import moment, { Moment } from "moment";
import React, { FC, Fragment } from "react";
import { CalendarEntry } from "./CalendarEntryHelper";
import CalendarNameColumn from "./CalendarNameColumn";
import CalendarRow from "./CalendarRow";
import CalendarSubHeadRow from "./CalendarSubHeadRow";

interface CalendarSchedulePhaseRowItem {
	calendarEntry: CalendarEntry;
	schedulePhase: SchedulePhase;
}

export interface CalendarSchedulePhaseRow {
	project: Project;
	items: CalendarSchedulePhaseRowItem[];
}

interface Props {
	startOfCalendar: Moment;
	endOfCalendar: Moment;
	nameWidth: number;
	isCalendarSubHeadOpen: IsCalendarSubHeadOpen;
	setIsCalendarSubHeadOpen: SetIsCalendarSubHeadOpen;
	renderEmptyRow: () => (JSX.Element | null)[] | JSX.Element;
	renderRow: (
		row: CalendarSchedulePhaseRow
	) => (JSX.Element | null)[] | JSX.Element;
}

const CalendarSchedulePhaseRows: FC<Props> = (props) => {
	const isInternalAdmin = useIsInternalAdmin();
	const classes = useStyles();
	const { t } = useTranslation();
	const projects = useProjects((state) => state.projects);
	const projectsError = useProjects((state) => state.error);
	const isInitializingProjects = useProjects((state) => state.isInitializing);
	const schedulePhases = useSchedulePhases((state) => state.items);
	const isInitializingSchedulePhases = useSchedulePhases(
		(state) => state.isInitializing
	);
	const schedulePhasesError = useSchedulePhases((state) => state.error);

	function getRows() {
		return schedulePhases
			.reduce<CalendarSchedulePhaseRow[]>((result, schedulePhase) => {
				const project = projects.find(
					(project) => project.id === schedulePhase.projectId
				);

				const calendarEntry = getCalendarEntryFromSchedulePhase(schedulePhase);

				if (calendarEntry && project?.projectType === ProjectType.PROJECT) {
					const startTime = moment.unix(calendarEntry.startTime);
					const endTime = moment.unix(calendarEntry.endTime);

					if (
						startTime.isBefore(props.endOfCalendar) &&
						endTime.isAfter(props.startOfCalendar)
					) {
						const existingRow = result.find(
							(row) => row.project.id === schedulePhase.projectId
						);

						if (existingRow) {
							existingRow.items.push({
								calendarEntry,
								schedulePhase,
							});
						} else {
							result.push({
								project,
								items: [
									{
										schedulePhase,
										calendarEntry,
									},
								],
							});
						}
					}
				}

				return result;
			}, [])
			.sort((a, b) => a.project.name.localeCompare(b.project.name));
	}

	const rows = getRows();
	const isLoading = isInitializingProjects || isInitializingSchedulePhases;
	const error = projectsError || schedulePhasesError;

	function renderRows() {
		if (isLoading) {
			return <LoadingIndicator />;
		} else if (error) {
			return <ErrorView error={error} />;
		} else if (rows.length === 0 && isInternalAdmin) {
			return (
				<Box className={classes.noProjectsBox}>
					<Alert severity="info" className={classes.noProjectsAlert}>
						<Box mb={px.l}>{t("CalendarSchedulePhaseRows_EmptyRow_Label")}</Box>
						<Button
							variant="contained"
							size="small"
							color="primary"
							onClick={() => {
								useSchedulePhaseModal.getState().openWithItem(
									{
										forSchedulePhase: {
											start: props.startOfCalendar.clone(),
											end: props.startOfCalendar.clone(),
										},
									},
									true
								);
							}}
						>
							{t("CalendarSchedulePhaseRows_EmptyRow_Button")}
						</Button>
					</Alert>
				</Box>
			);
		} else {
			return [...rows, null].map((row) => (
				<CalendarRow
					key={row?.project.id || "empty"}
					scheduleEntryTypesLength={0}
					isSingleRow
					renderColumns={() => (
						<Fragment>
							<CalendarNameColumn
								width={props.nameWidth}
								isBottomBorderTransparent={row !== null}
							/>
							{row ? props.renderRow(row) : props.renderEmptyRow()}
						</Fragment>
					)}
				/>
			));
		}
	}

	return (
		<Fragment>
			<CalendarSubHeadRow
				title={t("CalendarView_CalendarSubHead_SchedulePhase")}
				isOpen={props.isCalendarSubHeadOpen.schedulePhases}
				setIsOpen={(isOpen) =>
					props.setIsCalendarSubHeadOpen("schedulePhases", isOpen)
				}
			/>
			{props.isCalendarSubHeadOpen.schedulePhases && renderRows()}
		</Fragment>
	);
};

export default CalendarSchedulePhaseRows;

const useStyles = makeStyles({
	noProjectsBox: {
		borderBottom: `1px solid ${color.tableCellBorder}`,
		padding: px.m,
	},
	noProjectsAlert: {
		display: "flex",
		flex: 1,
	},
});
